// Set ağaç editörü (kural 15, mockup 08 orta panel): arama, tümünü aç/kapat, "Geçersiz formül"
// filtresi, ↑↓ taşıma (orderIndex taslak yazımı), kategori satırında "müşteri girebilir" anahtarı,
// kod benzersizliği rozeti. Finar rev-B 24: kirli taslakta ağacın altına yapışık kaydet çubuğu +
// "N değişiklik" listesi (SetChangesBar); rev-B 6: satır sonunda "Düzenle" (satır tıklaması da
// IndicatorModal'ı açar). Taslak/orijinal üst bileşende; burada yalnız görünüm + olaylar.
// Kırılım (spec §6.3 DR15): toplam satırı kalın + "kırılım: …" rozeti, grup satırı ince bant (birim/
// formül/değişim hücresiz, kendi erişim kutusu yok), üye satırı birimi üstten "(üstten)"; "+ Kırılım
// grubu ekle" / "+ Üye" satırın ⋯ menüsünde; üyesiz grup amber "Üye yok — + Üye ekle"; bir üye
// müşteriye kapalıysa toplam satırında "toplam portalda gizlenecek" ipucu.
// Sürükle-bırak (rev-2 #4b/#12b/#14): `onReorder` verilirse satırlar AYNI üst içinde sürüklenir →
// taslak `orderIndex` (↑↓ ile aynı yol; Kaydet/Vazgeç çubuğu değişmez).

import { useMemo, useState } from "react";
import type { KpiSetIndicator } from "@mahir/shared";
import {
  buildIndicatorTree,
  detectFormulaCycle,
  displayUnitFor,
  duplicateCodeIds,
  invalidFormulaIds,
  planTreeReorder,
  siblingsInTree,
  visibleIdsForSearch,
} from "../../lib/kpi-set-editor";
import { useRowDrag } from "../../hooks/use-row-drag";
import {
  memberHidesTotal,
  firstCalcMemberUnit,
  memberParentUnit,
  setBreakdownView,
  totalPillText,
} from "../../lib/kpi-breakdown-set";
import type { SetChange } from "../../lib/kpi-set-changes";
import { KPI_COPY } from "../../lib/kpi-ui";
import { type CalcGroupChange } from "../../lib/calc-group-change";
import { LegacyValuesNote } from "./LegacyValuesNote";
import { CalcGroupChangedBand } from "./CalcGroupChangedBand";
import { SetChangesBar } from "./SetChangesBar";
import { SetRowMenu } from "./SetRowMenu";
import { memberLevelOf, rowMenuItems, type GroupAddHandler } from "./set-tree-menu";
import { SetTreeOrderCell } from "./SetTreeRowActions";
import {
  bandRowBg,
  btnGhost,
  btnPrimary,
  btnSmall,
  codeStyle,
  inputStyle,
  monoStyle,
  pillStyle,
  selectedRowBg,
  tdStyle,
  thStyle,
} from "./kpi-form-styles";

export interface SetTreeEditorProps {
  indicators: KpiSetIndicator[];
  /** kod → birim (şablon çözümü için; KpiSetsPage modalla aynı haritayı geçirir) */
  setUnits?: ReadonlyMap<string, string | null>;
  selectedId: string | null;
  dirtyCount: number;
  /** rev-B 24 — bekleyen değişikliklerin insan dilindeki listesi (describeSetChanges) */
  changes: SetChange[];
  /** TODO-KPI-151 — taslakta hesap grubu değişen toplamlar */
  calcGroupChanges?: readonly CalcGroupChange[] | undefined;
  /** TODO-KPI-116 — eski (0069 öncesi) değeri olan kategori satırlarının metrik kimlikleri */
  legacyValueMetricIds?: ReadonlySet<string> | undefined;
  /** kısayol: kategoriyi toplam göstergesine dönüştür (Katalog sihirbazı) */
  onConvertLegacy?: ((metricId: string) => void) | undefined;
  saving: boolean;
  canEdit: boolean;
  /** kaydedilemeyen satır id'leri (sunucu hatası) */
  failedIds: ReadonlySet<string>;
  onSelect: (id: string | null) => void;
  onMove: (id: string, direction: -1 | 1) => void;
  /** sürükle-bırak: değişen taslak `orderIndex` yazımları (verilmezse sürükleme yok) */
  onReorder?: ((changes: Map<string, number>) => void) | undefined;
  onToggleClientOpen: (id: string, open: boolean) => void;
  onSave: () => void;
  onDiscard: () => void;
  onAdd: () => void;
  /** Kırılım: toplam/gösterge satırı ⋯ "+ Kırılım grubu ekle" (BreakdownGroupModal) */
  onAddBreakdownGroup?: GroupAddHandler | undefined;
  /** Kırılım: grup satırı ⋯ "+ Üye" / üyesiz grup "+ Üye ekle" */
  onAddBreakdownMember?: ((groupId: string) => void) | undefined;
  /** grup satırı ⋯ "Veri setinden kaldır" (grup IndicatorModal açmaz) */
  onRemoveRow?: ((id: string) => void) | undefined;
  /** yeni eklenen satırlar (kısa süre vurgulu) */
  flashIds?: ReadonlySet<string> | undefined;
}

export function SetTreeEditor(props: SetTreeEditorProps): JSX.Element {
  const {
    indicators,
    setUnits,
    selectedId,
    dirtyCount,
    changes,
    saving,
    canEdit,
    failedIds,
    onSelect,
    onMove,
    onReorder,
    onToggleClientOpen,
    onSave,
    onDiscard,
    onAdd,
    onAddBreakdownGroup,
    onAddBreakdownMember,
    onRemoveRow,
    flashIds,
    legacyValueMetricIds,
    onConvertLegacy,
  } = props;
  const [search, setSearch] = useState("");
  const [onlyInvalid, setOnlyInvalid] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

  const tree = useMemo(() => buildIndicatorTree(indicators), [indicators]);
  const bv = useMemo(() => setBreakdownView(indicators), [indicators]);
  const codes = useMemo(() => new Set(indicators.map((i) => i.code)), [indicators]);
  const invalid = useMemo(() => invalidFormulaIds(indicators, codes), [indicators, codes]);
  // P3-27: formül döngüsü istemcide de yakalanır — Kaydet pasifken SEBEP yazılır
  const cycle = useMemo(() => detectFormulaCycle(indicators), [indicators]);
  const dupes = useMemo(() => duplicateCodeIds(indicators), [indicators]);
  const visible = useMemo(() => visibleIdsForSearch(tree, search), [tree, search]);
  const drag = useRowDrag({
    enabled: canEdit && Boolean(onReorder),
    parentOf: (id) => siblingsInTree(tree, id)?.parentKey,
    canDrop: (a, b, pos) => planTreeReorder(tree, a, b, pos) !== null,
    onDrop: (a, b, pos) => {
      const changes = planTreeReorder(tree, a, b, pos);
      if (changes) onReorder?.(changes);
    },
  });

  const filtering = visible !== null || onlyInvalid;
  const rows = useMemo(() => {
    const out: typeof tree.rows = [];
    const hiddenUnder = new Set<string>();
    // geçersiz filtresi: geçersiz satır + ataları görünür
    let invalidVisible: Set<string> | null = null;
    if (onlyInvalid) {
      invalidVisible = new Set();
      for (const r of tree.rows) {
        if (!invalid.has(r.node.id)) continue;
        let cur: (typeof tree.rows)[number] | undefined = r;
        while (cur) {
          invalidVisible.add(cur.node.id);
          cur = cur.node.parentId ? tree.byId.get(cur.node.parentId) : undefined;
        }
      }
    }
    for (const r of tree.rows) {
      if (visible && !visible.has(r.node.id)) continue;
      if (invalidVisible && !invalidVisible.has(r.node.id)) continue;
      if (!filtering && r.node.parentId && hiddenUnder.has(r.node.parentId)) {
        hiddenUnder.add(r.node.id);
        continue;
      }
      out.push(r);
      if (!filtering && r.hasChildren && collapsed.has(r.node.id)) hiddenUnder.add(r.node.id);
    }
    return out;
  }, [tree, visible, onlyInvalid, invalid, collapsed, filtering]);

  const categoryIds = useMemo(
    // grup bandı aç/kapanmaz (DR11) — "Kapat" yalnız kategori ve toplamları katlar
    () =>
      new Set(
        tree.rows
          .filter((r) => r.hasChildren && bv.kindOf(r.node.id) !== "breakdown_group")
          .map((r) => r.node.id),
      ),
    [tree, bv],
  );

  function expandAll(): void {
    setCollapsed(new Set());
  }
  function collapseAll(): void {
    setCollapsed(new Set(categoryIds));
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <input
          type="search"
          aria-label="Ağaçta ara"
          placeholder="Ağaçta ara…"
          style={{ ...inputStyle, width: 220 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button type="button" style={btnGhost} onClick={expandAll}>
          Tümünü gör
        </button>
        <button type="button" style={btnGhost} onClick={collapseAll}>
          Kapat
        </button>
        <button
          type="button"
          aria-pressed={onlyInvalid}
          onClick={() => setOnlyInvalid((v) => !v)}
          style={{
            ...btnGhost,
            ...(onlyInvalid
              ? { borderColor: "var(--color-error)", color: "var(--color-error)" }
              : {}),
          }}
        >
          Geçersiz formül ({invalid.size})
        </button>
        <span style={{ flex: 1 }} />
        {canEdit ? (
          <button type="button" style={btnPrimary} onClick={onAdd}>
            ＋ Gösterge
          </button>
        ) : null}
      </div>

      {/* UX-31: konumlu kap — içindeki `sr-only` (absolute) öğeler yatay kaydırma klibini aşıp belge genişliğini şişirirdi */}
      <div style={{ overflowX: "auto", position: "relative" }}>
        <table
          role="treegrid"
          aria-label="Veri seti göstergeleri"
          style={{ width: "100%", borderCollapse: "collapse" }}
        >
          <thead>
            <tr>
              <th style={thStyle}>Gösterge</th>
              <th style={thStyle}>Birim</th>
              <th style={thStyle}>Formül</th>
              {/* Finar rev-A 7 — sütun göstergenin Değişim kolonunu aç/kapat ayarıdır (thStyle büyük harf) */}
              <th style={thStyle}>{KPI_COPY.changeColumnToggle}</th>
              <th style={thStyle}>Müşteri</th>
              <th style={{ ...thStyle, width: 90 }}>Sıra</th>
              <th style={{ ...thStyle, width: 1 }}>
                <span className="sr-only">{KPI_COPY.editIndicator}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td style={{ ...tdStyle, color: "var(--color-text-3)" }} colSpan={7}>
                  {indicators.length === 0 ? "Veri setinde gösterge yok." : "Eşleşen gösterge yok."}
                </td>
              </tr>
            ) : null}
            {rows.map((r) => {
              const ind = r.node;
              const kind = bv.kindOf(ind.id);
              const isGroup = kind === "breakdown_group";
              const isTotal = kind === "total";
              const isCat = kind === "category";
              // birim/formül/değişim hücresi olmayan yapı satırı (kategori bandı / grup bandı)
              const structural = isCat || isGroup;
              const flashed = flashIds?.has(ind.id) === true;
              const legacy = isCat && legacyValueMetricIds?.has(ind.metricId) === true;
              const menu = canEdit
                ? rowMenuItems(
                    ind.id,
                    kind,
                    bv.isMember(ind.id),
                    {
                      onAddBreakdownGroup,
                      onAddBreakdownMember,
                      onRemoveRow,
                      onConvertLegacy:
                        legacy && onConvertLegacy ? () => onConvertLegacy(ind.metricId) : undefined,
                    },
                    memberLevelOf(ind.id, bv),
                  )
                : [];
              const selected = ind.id === selectedId;
              const open = !collapsed.has(ind.id);
              const active = ind.formulas.find((f) => f.isActive);
              const bad = invalid.has(ind.id);
              const failed = failedIds.has(ind.id);
              const clientOpen = ind.clientAccess === "open";
              return (
                <tr
                  key={ind.id}
                  {...(drag.rowProps(ind.id) ?? {})}
                  role="row"
                  aria-level={r.depth + 1}
                  aria-selected={selected}
                  onClick={() => onSelect(ind.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelect(ind.id);
                    }
                  }}
                  tabIndex={0}
                  style={{
                    cursor: "pointer",
                    background: selected
                      ? selectedRowBg
                      : flashed
                        ? "color-mix(in srgb, var(--color-primary) 10%, white)"
                        : isCat
                          ? bandRowBg
                          : "transparent",
                    ...(isGroup
                      ? {
                          borderTop:
                            "1px solid color-mix(in srgb, var(--color-border) 40%, transparent)",
                        }
                      : {}),
                  }}
                  data-row-kind={kind}
                >
                  <td style={{ ...tdStyle, paddingLeft: 12 + r.depth * 22 }}>
                    <span className="flex items-center gap-1">
                      {r.hasChildren && !isGroup ? (
                        <button
                          type="button"
                          aria-label={open ? "Kapat" : "Aç"}
                          aria-expanded={open}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCollapsed((prev) => {
                              const next = new Set(prev);
                              if (next.has(ind.id)) next.delete(ind.id);
                              else next.add(ind.id);
                              return next;
                            });
                          }}
                          style={{
                            border: 0,
                            background: "transparent",
                            width: 22,
                            minHeight: 28,
                            cursor: "pointer",
                            color: "var(--color-text-2)",
                          }}
                        >
                          {open ? "▾" : "▸"}
                        </button>
                      ) : (
                        <span style={{ width: 22 }} aria-hidden="true" />
                      )}
                      <span
                        style={
                          isGroup
                            ? { fontSize: "0.75rem", fontWeight: 600, color: "var(--color-text-3)" }
                            : { fontWeight: isCat ? 600 : isTotal ? 700 : 400 }
                        }
                      >
                        {ind.label}
                      </span>
                      {ind.isDerived ? (
                        <span style={pillStyle} title="Hesaplanan gösterge">
                          ƒx
                        </span>
                      ) : null}
                      <span style={codeStyle}>{ind.code}</span>
                      {isCat ? (
                        <span className="text-xs" style={{ color: "var(--color-text-3)" }}>
                          · {r.leafCount} gösterge
                        </span>
                      ) : null}
                      {legacy ? (
                        <LegacyValuesNote
                          canEdit={canEdit}
                          onConvert={
                            onConvertLegacy ? () => onConvertLegacy(ind.metricId) : undefined
                          }
                        />
                      ) : null}
                      {isTotal ? (
                        <span style={pillStyle} data-testid="kpi-set-breakdown-pill">
                          {totalPillText(ind, bv)}
                        </span>
                      ) : null}
                      {isGroup && bv.membersOf(ind.id).length === 0 ? (
                        <button
                          type="button"
                          className="text-xs"
                          data-testid="kpi-set-group-empty"
                          disabled={!canEdit || !onAddBreakdownMember}
                          onClick={(e) => {
                            e.stopPropagation();
                            onAddBreakdownMember?.(ind.id);
                          }}
                          onKeyDown={(e) => e.stopPropagation()}
                          style={{
                            border: "1px solid var(--color-warning-border)",
                            background: "var(--color-warning-bg)",
                            color: "var(--color-warning-text)",
                            borderRadius: 999,
                            padding: "2px 8px",
                            cursor: canEdit ? "pointer" : "default",
                          }}
                        >
                          {KPI_COPY.breakdown.tree.emptyGroup}
                        </button>
                      ) : null}
                      {dupes.has(ind.id) ? (
                        <span
                          style={{
                            ...pillStyle,
                            color: "var(--color-error)",
                            background: "var(--color-warning-bg)",
                          }}
                        >
                          mükerrer kod
                        </span>
                      ) : null}
                      {bad ? (
                        <span
                          style={{
                            ...pillStyle,
                            color: "var(--color-error)",
                            background: "var(--color-warning-bg)",
                          }}
                        >
                          geçersiz formül
                        </span>
                      ) : null}
                      {failed ? (
                        <span
                          style={{
                            ...pillStyle,
                            color: "var(--color-error)",
                            background: "var(--color-warning-bg)",
                          }}
                        >
                          kaydedilemedi
                        </span>
                      ) : null}
                    </span>
                  </td>
                  <UnitCell
                    isCat={structural}
                    unit={ind.unit}
                    setUnits={setUnits}
                    inheritedUnit={bv.isMember(ind.id) ? memberParentUnit(ind, bv) : undefined}
                    firstChildUnit={firstCalcMemberUnit(ind, bv)}
                  />
                  <td
                    style={{
                      ...tdStyle,
                      ...monoStyle,
                      color: "var(--color-text-2)",
                      maxWidth: 260,
                    }}
                  >
                    <span className="block truncate" title={active?.expression ?? ""}>
                      {structural ? "" : (active?.expression ?? "—")}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    {structural ? (
                      ""
                    ) : (
                      <span
                        className="text-xs"
                        style={{
                          color: ind.showChange ? "var(--color-primary)" : "var(--color-text-3)",
                        }}
                      >
                        {ind.showChange ? "● gösterilir" : "○ gizli"}
                      </span>
                    )}
                  </td>
                  <td style={tdStyle} onClick={(e) => e.stopPropagation()}>
                    {isCat ? (
                      <label
                        className="flex items-center gap-1 text-xs"
                        style={{ color: "var(--color-text-2)", minHeight: 28, cursor: "pointer" }}
                      >
                        <input
                          type="checkbox"
                          checked={clientOpen}
                          disabled={!canEdit}
                          onChange={(e) => onToggleClientOpen(ind.id, e.target.checked)}
                          aria-label={`${ind.label}: müşteri girebilir`}
                        />
                        müşteri girebilir
                      </label>
                    ) : (
                      <span className="text-xs" style={{ color: "var(--color-text-3)" }}>
                        {ind.clientAccess === "inherit"
                          ? `üstten (${ind.effectiveClientAccess === "open" ? "açık" : ind.effectiveClientAccess === "readonly" ? "görüntüler" : "kapalı"})`
                          : ind.clientAccess === "open"
                            ? "açık"
                            : ind.clientAccess === "readonly"
                              ? "görüntüler"
                              : "kapalı"}
                      </span>
                    )}
                    {isTotal && memberHidesTotal(ind, bv) ? (
                      <span
                        className="block text-xs"
                        data-testid="kpi-set-total-hidden-hint"
                        style={{ color: "var(--color-warning-text)" }}
                      >
                        {KPI_COPY.breakdown.tree.memberHidesTotal}
                      </span>
                    ) : null}
                  </td>
                  <SetTreeOrderCell
                    label={ind.label}
                    canEdit={canEdit}
                    draggable={canEdit && Boolean(onReorder)}
                    onMove={(d) => onMove(ind.id, d)}
                  />
                  <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                    <SetRowMenu label={ind.label} items={menu} />{" "}
                    <button
                      type="button"
                      className="kpi-row-action"
                      data-testid="kpi-set-row-edit"
                      aria-label={KPI_COPY.editIndicatorAria(ind.label)}
                      style={{ ...btnSmall, padding: "2px 10px" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelect(ind.id);
                      }}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      {KPI_COPY.editIndicator}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {canEdit && onReorder ? (
        <span id={drag.hintId} className="sr-only">
          {KPI_COPY.reorder.hint}
        </span>
      ) : null}
      {/* rev-B N2 — kirli durum duyurusu: canlı bölge hep DOM'da (çubuk takılıp sökülür) */}
      <span className="sr-only" aria-live="polite" data-testid="kpi-set-dirty-live">
        {dirtyCount > 0
          ? `${KPI_COPY.setChanges.bar}: ${KPI_COPY.setChanges.count(changes.length || dirtyCount)}`
          : ""}
      </span>
      <CalcGroupChangedBand changes={props.calcGroupChanges} />
      {dirtyCount > 0 ? (
        <SetChangesBar
          changes={changes}
          dirtyCount={dirtyCount}
          saving={saving}
          cycle={cycle}
          onSave={onSave}
          onDiscard={onDiscard}
        />
      ) : null}
    </div>
  );
}

/** Birim hücresi — türetilmiş göstergenin birim şablonu çözülmüş gösterilir. */
export function UnitCell(props: {
  isCat: boolean;
  unit: string | null;
  setUnits: ReadonlyMap<string, string | null> | undefined;
  /** kırılım üyesi: birim üst göstergeden (§15/1) — "kişi (üstten)" */
  inheritedUnit?: string | null | undefined;
  /** `{}` / `{_alt}` şablonu: hesap grubunun ilk üyesinin birimi (TODO-KPI-126) */
  firstChildUnit?: string | null | undefined;
}): JSX.Element {
  if (props.isCat) return <td style={tdStyle}></td>;
  if (props.inheritedUnit !== undefined) {
    const d = displayUnitFor({ unit: props.inheritedUnit }, props.setUnits);
    return (
      <td style={{ ...tdStyle, color: "var(--color-text-3)" }} data-testid="kpi-set-unit-inherited">
        {d.text} {KPI_COPY.breakdown.tree.inheritedUnit}
      </td>
    );
  }
  const d = displayUnitFor({ unit: props.unit }, props.setUnits, props.firstChildUnit);
  if (d.unresolved) {
    return (
      <td
        style={{ ...tdStyle, color: "var(--color-warning-text)" }}
        title="birim şablonu çözülemedi"
      >
        {d.text}
      </td>
    );
  }
  return (
    <td style={tdStyle} {...(d.template !== null ? { title: `Birim şablonu: ${d.template}` } : {})}>
      {d.text}
    </td>
  );
}

export { rowMenuItems };
